import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useClickAlarmMutation } from "./notification.queries"
import { usePushAlarmMutation } from "./notification.queries"
import {
  NotificationKindEnum,
  type NotificationList,
  type PushMsgList,
} from "./notification.types"
import { NOTIFICATION_API_PREFIX } from "@/shared/lib/axios/axios.constants"
import { getAccessToken } from "@/shared/lib/token/token"
import { camelizeKeys } from "humps"
import { ProjectQueryKeysEnum } from "@/entities/project/project.constants"
import { TranslationQueryKeysEnum } from "@/entities/translation/translation.constants"
import { useAuthStore } from "@/entities/auth/auth.model"
import { createAuthenticatedEventSource } from "@/shared/lib/event-source/event-source"

export const useNotification = () => {
  const { isAnonymousUser } = useAuthStore()
  const [isNotiOpen, setIsNotiOpen] = useState(false)
  const [isUnreadNotiExists, setIsUnreadNotiExists] = useState(false)
  const [notiList, setNotiList] = useState<PushMsgList[]>([])
  const queryClient = useQueryClient()

  const { mutate: pushAlarm } = usePushAlarmMutation({
    onSuccess: () => setIsUnreadNotiExists(false),
  })

  const { mutate: clickAlarm, status: clickAlarmStatus } =
    useClickAlarmMutation({})

  useEffect(() => {
    if (isAnonymousUser) return
    const eventSource = createAuthenticatedEventSource({
      url: `${import.meta.env.VITE_BASE_API_URL}${NOTIFICATION_API_PREFIX}/push_msg`,
      token: getAccessToken(),
      stream: "notification_push",
    })
    eventSource.onmessage = (event) => {
      const data = camelizeKeys(JSON.parse(event.data)) as NotificationList & {
        readAt?: string
      }
      if (
        (data.readAt &&
          data.pushMsgList.length > 0 &&
          new Date(data.readAt) < new Date(data.pushMsgList[0].createDt)) ||
        !data.readAt
      ) {
        setIsUnreadNotiExists(true)
      }
      if (data.readAt) {
        setNotiList(data.pushMsgList)
      } else {
        setNotiList((prevNotiList) => {
          const newNotiList = data.pushMsgList.filter(
            (newNoti) =>
              !prevNotiList.some((prevNoti) => prevNoti.id === newNoti.id)
          )
          return [...newNotiList, ...prevNotiList]
        })
      }
      queryClient.invalidateQueries({
        queryKey: [ProjectQueryKeysEnum.FetchProjectList],
      })
      if (
        data.pushMsgList.some(
          (message) => message.kind === NotificationKindEnum.CATWorkflowAssigned
        )
      ) {
        void queryClient.invalidateQueries({
          queryKey: [TranslationQueryKeysEnum.FetchAssignedCatWorkspaces],
        })
      }
    }
    eventSource.onerror = () => {
      eventSource.close()
    }

    return () => eventSource.close()
  }, [clickAlarmStatus, isAnonymousUser, queryClient])

  return {
    isNotiOpen,
    setIsNotiOpen,
    isUnreadNotiExists,
    notiList,
    pushAlarm,
    clickAlarm,
  }
}
